import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { lazy, Suspense, useEffect, useState } from "react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { getAlbumDetail, type DiscogsAlbum } from "@/lib/discogs.functions";
import { getAlbumCommunity } from "@/lib/album-community.functions";
import { getAlbumPricing } from "@/lib/album-pricing.functions";
import { Tracklist } from "@/components/Tracklist";
import { Star, CalendarDays, Globe2, LibraryBig, Mic2, SlidersHorizontal, Palette, Disc3 } from "lucide-react";
import { getAlbumRating } from "@/lib/ratings.functions";
import { AlbumRaters } from "@/components/ratings/AlbumRaters";
import { AlbumPageSkeleton } from "@/components/album/AlbumPageSkeleton";
import { ArtistChip } from "@/components/album/ArtistChip";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";


const AlbumComments = lazy(() =>
  import("@/components/AlbumComments").then((m) => ({ default: m.AlbumComments })),
);
const CommunityPricingChart = lazy(() =>
  import("@/components/album/CommunityPricingChart").then((m) => ({
    default: m.CommunityPricingChart,
  })),
);
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  addAlbum,
  removeAlbum,
  updateAlbumDetails,
  getMyAlbumByDiscogs,
  type AlbumStatus,
  type Listing,
} from "@/lib/collection.functions";

import { useCurrentUser } from "@/hooks/useCurrentUser";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogFooter,
} from "@/components/ui/responsive-dialog";
import { Heart, Plus, Repeat2, Tag, Users as UsersIcon, Loader2, X, TrendingUp } from "lucide-react";
import { buildAlbumParam, extractAlbumId } from "@/lib/slugify";
import { cn } from "@/lib/utils";
import { reportWrongCover } from "@/lib/cover-report.functions";
import { ImageOff } from "lucide-react";


type Action = "owned" | "wishlist" | "for_trade" | "for_sale";


export const Route = createFileRoute("/album/$id")({
  // Sem espera bloqueante na navegação: o loader só aquece o cache/head no SSR.
  // No cliente o skeleton aparece de imediato via pendingComponent.
  pendingMs: 0,
  pendingMinMs: 300,
  pendingComponent: () => <AlbumPageSkeleton />,
  loader: async ({ params, context }) => {
    const discogsId = extractAlbumId(params.id);
    if (!discogsId) return { album: null, discogsId: "" };
    try {
      const album = await context.queryClient.ensureQueryData({
        queryKey: ["album", "detail", discogsId],
        queryFn: () => getAlbumDetail({ data: { discogsId } }),
        staleTime: 1000 * 60 * 60,
      });
      return { album, discogsId };
    } catch {
      return { album: null, discogsId };
    }
  },

  head: ({ params, loaderData }) => {
    const a = loaderData?.album as
      | { title: string; artist: string; year?: number | null; cover?: string | null; genre?: string | null; label?: string | null }
      | null
      | undefined;
    const discogsId = loaderData?.discogsId || extractAlbumId(params.id);
    const canonicalParam = a ? buildAlbumParam(discogsId, a.artist, a.title) : discogsId;
    const canonical = `https://viny.club/album/${canonicalParam}`;
    if (!a) {
      return {
        meta: [
          { title: "Ficha do disco — Viny Club" },
          { name: "description", content: "Ficha técnica, prensagens e colecionadores deste disco no Viny Club." },
          { property: "og:url", content: canonical },
          { property: "og:type", content: "music.album" },
        ],
        links: [{ rel: "canonical", href: canonical }],
      };
    }
    const title = `${a.title} — ${a.artist} | Ficha do disco no Viny Club`;
    const description = `Ficha do disco ${a.title} de ${a.artist}${a.year ? ` (${a.year})` : ""}${a.label ? `, gravadora ${a.label}` : ""}. Veja capa, faixas, prensagens e colecionadores no Viny Club.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "music.album" },
        { property: "og:url", content: canonical },
        ...(a.cover ? [{ property: "og:image", content: a.cover }] : []),
        ...(a.cover ? [{ name: "twitter:card", content: "summary_large_image" }] : []),
        ...(a.cover ? [{ name: "twitter:image", content: a.cover }] : []),
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MusicAlbum",
            name: a.title,
            byArtist: { "@type": "MusicGroup", name: a.artist },
            datePublished: a.year ? String(a.year) : undefined,
            recordLabel: a.label || undefined,
            genre: a.genre || undefined,
            image: a.cover || undefined,
            url: canonical,
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <AppShell>
      <div className="mx-auto max-w-md px-4 py-24 text-center">
        <h1 className="font-display text-2xl font-bold">Álbum não encontrado</h1>
      </div>
    </AppShell>
  ),
  errorComponent: () => (
    <AppShell>
      <div className="mx-auto max-w-md px-4 py-24 text-center">
        <h1 className="font-display text-2xl font-bold">Erro ao carregar álbum</h1>
      </div>
    </AppShell>
  ),
  component: AlbumPage,
});

function AlbumPage() {
  const { id } = Route.useParams();
  const albumId = extractAlbumId(String(id ?? ""));
  const fetchDetail = useServerFn(getAlbumDetail);

  const detail = useQuery({
    queryKey: ["album", "detail", albumId],
    queryFn: () => fetchDetail({ data: { discogsId: albumId } }),
    enabled: albumId.length > 0,
    staleTime: 1000 * 60 * 60,
  });

  const navigate = useNavigate();
  // Redirect legacy /album/<id> URLs to the canonical /album/<slug>--<id> form.
  useEffect(() => {
    const a = detail.data;
    if (!a || !albumId) return;
    const canonicalParam = buildAlbumParam(albumId, a.artist, a.title);
    if (canonicalParam && canonicalParam !== String(id)) {
      navigate({ to: "/album/$id", params: { id: canonicalParam }, replace: true });
    }
  }, [detail.data, albumId, id, navigate]);

  const { isAuthenticated } = useCurrentUser();
  const qc = useQueryClient();
  const addFn = useServerFn(addAlbum);
  const removeFn = useServerFn(removeAlbum);
  const updateFn = useServerFn(updateAlbumDetails);
  const lookupFn = useServerFn(getMyAlbumByDiscogs);
  const [authOpen, setAuthOpen] = useState(false);
  const [actionDialog, setActionDialog] = useState<Action | null>(null);
  const [priceDialog, setPriceDialog] = useState(false);
  const [paidPrice, setPaidPrice] = useState("");
  const [purchasedAt, setPurchasedAt] = useState("");
  const [salePrice, setSalePrice] = useState("");
  const [saleDescription, setSaleDescription] = useState("");
  const [shareThought, setShareThought] = useState(false);

  const myAlbumQ = useQuery({
    queryKey: ["my-album", albumId],
    queryFn: () => lookupFn({ data: { discogsId: albumId } }),
    enabled: isAuthenticated && albumId.length > 0,
  });
  const communityFn = useServerFn(getAlbumCommunity);
  const communityQ = useQuery({
    queryKey: ["album", "community", albumId],
    queryFn: () => communityFn({ data: { discogsId: albumId } }),
    enabled: albumId.length > 0,
    staleTime: 1000 * 60,
  });
  const pricingFn = useServerFn(getAlbumPricing);
  const pricingQ = useQuery({
    queryKey: ["album", "pricing", albumId],
    queryFn: () =>
      pricingFn({
        data: {
          discogsId: albumId,
          artist: detail.data?.artist ?? "",
          title: detail.data?.title ?? "",
        },
      }),
    enabled: albumId.length > 0,
    staleTime: 1000 * 60 * 10,
  });
  const mine = myAlbumQ.data;

  // Reportar capa errada: reexecuta a busca automática da capa correta.
  const reportCoverFn = useServerFn(reportWrongCover);
  const [coverOverride, setCoverOverride] = useState<string | null>(null);
  const reportCoverMut = useMutation({
    mutationFn: () =>
      reportCoverFn({
        data: {
          discogsId: albumId,
          artist: detail.data?.artist ?? "",
          title: detail.data?.title ?? "",
          currentCover: coverOverride ?? detail.data?.cover ?? "",
        },
      }),
    onSuccess: (res) => {
      if (res.ok && res.cover) {
        setCoverOverride(res.cover);
        toast.success("Capa corrigida — obrigado por reportar!");
        qc.invalidateQueries({ queryKey: ["album", "detail", albumId] });
        qc.invalidateQueries({ queryKey: ["my-album", albumId] });
        qc.invalidateQueries({ queryKey: ["collection"] });
      } else {
        toast.info("Não encontramos outra capa confiável para este disco.");
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const isOwned = mine?.status === "owned";
  const isWishlist = mine?.status === "wishlist";
  const isForSale = mine?.listing === "for_sale";
  const isForTrade = mine?.listing === "for_trade";

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["my-album", albumId] });
    qc.invalidateQueries({ queryKey: ["my-collection"] });
    qc.invalidateQueries({ queryKey: ["my-wishlist"] });
    qc.invalidateQueries({ queryKey: ["my-albums"] });
    qc.invalidateQueries({ queryKey: ["marketplace"] });
    qc.invalidateQueries({ queryKey: ["album", "community", albumId] });
  };

  const removeMut = useMutation({
    mutationFn: async (action: Action) => {
      if (!mine) return;
      if (action === "for_sale" || action === "for_trade") {
        await updateFn({
          data: { id: mine.id, listing: "none", sale_price: null, sale_description: null },
        });
      } else {
        await removeFn({ data: { id: mine.id } });
      }
    },
    onSuccess: (_r, action) => {
      const labels: Record<Action, string> = {
        owned: "Removido da coleção",
        wishlist: "Removido da wishlist",
        for_trade: "Retirado da troca",
        for_sale: "Retirado da venda",
      };
      toast.success(labels[action]);
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });


  const addMut = useMutation({
    mutationFn: (action: Action) => {
      const a = detail.data!;
      const status: AlbumStatus = action === "wishlist" ? "wishlist" : "owned";
      const listing: Listing =
        action === "for_sale" ? "for_sale" : action === "for_trade" ? "for_trade" : "none";
      return addFn({
        data: {
          discogsId: albumId,
          status,
          listing,
          title: a.title,
          artist: a.artist,
          year: a.year || null,
          cover: a.cover ?? "",
          genres: a.genre ? [a.genre] : [],
          paid_price: action === "owned" && paidPrice ? Number(paidPrice) : null,
          purchased_at: action === "owned" && purchasedAt ? purchasedAt : null,
          sale_price: action === "for_sale" && salePrice ? Number(salePrice) : null,
          sale_description:
            (action === "for_sale" || action === "for_trade") && saleDescription
              ? saleDescription
              : null,
        },
      });
    },
    onSuccess: (_r, action) => {
      const labels: Record<Action, string> = {
        owned: "Adicionado à coleção",
        wishlist: "Adicionado à wishlist",
        for_trade: "Disponível para troca",
        for_sale: "Disponível para venda",
      };
      toast.success(labels[action]);
      setActionDialog(null);
      setPaidPrice("");
      setPurchasedAt("");
      setSalePrice("");
      setSaleDescription("");
      invalidate();
      if (shareThought && detail.data) {
        window.sessionStorage.setItem(
          "viny:share-album",
          JSON.stringify({
            discogsId: albumId,
            title: detail.data.title,
            artist: detail.data.artist,
            cover: detail.data.cover ?? "",
            year: detail.data.year || null,
          }),
        );
        setShareThought(false);
        navigate({ to: "/explore", search: { tab: "feed" } });
      }
    },
    onError: (e: Error) => {
      if (e.message.includes("Unauthorized")) setAuthOpen(true);
      else if (e.message.includes("free_marketplace_blocked"))
        toast.error("Marketplace é exclusivo do Premium.");
      else toast.error(e.message);
    },
  });

  const isActive = (action: Action) =>
    action === "owned"
      ? isOwned
      : action === "wishlist"
        ? isWishlist
        : action === "for_sale"
          ? isForSale
          : isForTrade;

  const priceMut = useMutation({
    mutationFn: async () => {
      if (!mine) return;
      await updateFn({
        data: {
          id: mine.id,
          paid_price: paidPrice ? Number(paidPrice) : null,
          purchased_at: purchasedAt ? purchasedAt : null,
        },
      });
    },
    onSuccess: () => {
      toast.success("Valor registrado — obrigado por ajudar a comunidade!");
      setPriceDialog(false);
      invalidate();
      qc.invalidateQueries({ queryKey: ["album", "pricing", albumId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const handleContributePrice = () => {
    if (!isAuthenticated) {
      setAuthOpen(true);
      return;
    }
    if (mine) {
      setPaidPrice(mine.paid_price != null ? String(mine.paid_price) : "");
      setPurchasedAt(mine.purchased_at ?? "");
      setPriceDialog(true);
      return;
    }
    setPaidPrice("");
    setPurchasedAt("");
    setActionDialog("owned");
  };

  const handleAction = (action: Action) => {
    if (!isAuthenticated) {
      setAuthOpen(true);
      return;
    }
    if (isActive(action)) {
      removeMut.mutate(action);
      return;
    }
    if (action === "wishlist") {
      addMut.mutate(action);
      return;
    }
    setActionDialog(action);
  };


  if (!albumId) {
    return (
      <AppShell>
        <div className="mx-auto max-w-md px-4 py-24 text-center">
          <h1 className="font-display text-2xl font-bold">Álbum não encontrado</h1>
        </div>
      </AppShell>
    );
  }


  if (detail.isLoading) {
    return <AlbumPageSkeleton />;
  }


  if (detail.isError || !detail.data) {
    return (
      <AppShell>
        <div className="mx-auto max-w-md px-4 py-24 text-center">
          <h1 className="font-display text-2xl font-bold">Álbum não encontrado</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Não conseguimos carregar esse disco.
          </p>
        </div>
      </AppShell>
    );
  }

  const album = detail.data;
  const cover = coverOverride ?? album.cover;
  const tracklist: { pos: string; title: string; duration: string }[] = album.tracklist ?? [];

  return (
    <AppShell>
      <div className="relative overflow-hidden">
        {cover && (
          <img loading="lazy" decoding="async" src={cover} alt="" className="absolute inset-0 h-full w-full object-cover blur-3xl opacity-40" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/80 to-background" />
        <div className="relative mx-auto max-w-7xl px-4 py-8 md:grid md:max-w-7xl md:grid-cols-[auto_1fr] md:gap-8 md:py-12 lg:px-8 lg:py-16">
          <div className="float-left mr-4 mb-3 w-28 md:float-none md:mr-0 md:mb-0 md:flex md:w-56 md:flex-col md:gap-2 lg:w-72">
            {cover && (
              <img loading="lazy" decoding="async" src={cover} alt={album.title} className="aspect-square w-full rounded-xl shadow-2xl shadow-black/60 md:rounded-2xl" />
            )}
            <button
              type="button"
              onClick={() => {
                if (!isAuthenticated) {
                  setAuthOpen(true);
                  return;
                }
                reportCoverMut.mutate();
              }}
              disabled={reportCoverMut.isPending}
              className="hidden items-center justify-center gap-1.5 rounded-lg border border-border/60 px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-surface hover:text-foreground disabled:opacity-60 md:inline-flex"
            >
              {reportCoverMut.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <ImageOff className="h-3.5 w-3.5" />
              )}
              {reportCoverMut.isPending ? "Buscando capa certa…" : "Reportar capa errada"}
            </button>
          </div>
          <div className="flex min-w-0 flex-col justify-end">
            <h1 className="font-display text-xl font-bold leading-tight md:mt-2 md:text-4xl lg:text-6xl">{album.title}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground md:mt-2 md:text-lg">
              <ArtistChip name={album.artist} />
              <span>
                {album.year ? `· ${album.year}` : ""}
                <span className="hidden md:inline">{album.country ? ` · ${album.country}` : ""}</span>
              </span>
            </div>

            <div className="clear-both grid grid-cols-2 gap-2 pt-4 md:mt-6 md:flex md:flex-wrap md:pt-0">

              <ActionButton
                action="owned"
                label="Tenho"
                Icon={Plus}
                active={isOwned}
                pending={
                  (addMut.isPending && addMut.variables === "owned") ||
                  (removeMut.isPending && removeMut.variables === "owned")
                }
                onClick={() => handleAction("owned")}
                primary
              />
              <ActionButton
                action="wishlist"
                label="Quero"
                Icon={Heart}
                active={isWishlist}
                pending={
                  (addMut.isPending && addMut.variables === "wishlist") ||
                  (removeMut.isPending && removeMut.variables === "wishlist")
                }
                onClick={() => handleAction("wishlist")}
              />
              <ActionButton
                action="for_trade"
                label="Trocar"
                Icon={Repeat2}
                active={isForTrade}
                pending={
                  (addMut.isPending && addMut.variables === "for_trade") ||
                  (removeMut.isPending && removeMut.variables === "for_trade")
                }
                onClick={() => handleAction("for_trade")}
              />
              <ActionButton
                action="for_sale"
                label="Vender"
                Icon={Tag}
                active={isForSale}
                pending={
                  (addMut.isPending && addMut.variables === "for_sale") ||
                  (removeMut.isPending && removeMut.variables === "for_sale")
                }
                onClick={() => handleAction("for_sale")}
              />
            </div>

            <AlbumScoreHero discogsId={albumId} trackCount={tracklist.length} className="hidden md:inline-flex" />

            <AvailabilityHighlight data={communityQ.data} loading={communityQ.isLoading} />

          </div>
        </div>
        {/* Nota da comunidade: largura total abaixo da capa no mobile */}
        <div className="relative mx-auto max-w-7xl px-4 pb-6 md:hidden">
          <AlbumScoreHero discogsId={albumId} trackCount={tracklist.length} className="mt-0 flex w-full" />
        </div>
      </div>

      {/* Navegação por etapas (mobile) */}
      <nav className="sticky top-0 z-20 -mx-0 border-b border-border/60 bg-background/85 backdrop-blur md:hidden">
        <div className="flex gap-2 overflow-x-auto px-4 py-2.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {[
            ["#tracklist", "Faixas"],
            ["#comunidade-tabs", "Comunidade"],
            ["#precos-comunidade", "Preços"],
            ["#comentarios", "Comentários"],
            ["#informacoes", "Ficha"],
          ].map(([href, label]) => (
            <a
              key={href}
              href={href}
              className="shrink-0 rounded-full border border-border bg-surface px-3.5 py-1.5 text-[13px] font-semibold text-muted-foreground"
            >
              {label}
            </a>
          ))}
        </div>
      </nav>

      <div className="mx-auto grid max-w-7xl grid-cols-[minmax(0,1fr)] gap-8 px-4 py-6 md:gap-10 md:py-10 lg:grid-cols-[minmax(0,1fr)_320px] lg:px-8">
        <div className="min-w-0 space-y-10">
          <section>
            <h2 id="tracklist" className="mb-4 scroll-mt-16 font-display text-xl font-bold md:text-2xl">Faixas</h2>
            <div className="overflow-hidden rounded-2xl border border-border bg-surface">
              {tracklist.length === 0 ? (
                <div className="px-5 py-5 text-sm text-muted-foreground">Faixas não informadas nesta edição.</div>
              ) : (
                <Tracklist
                  artist={album.artist}
                  albumTitle={album.title}
                  tracks={tracklist}
                  discogsId={albumId}
                />
              )}
            </div>
            <AlbumRaters discogsId={albumId} />
          </section>


          <section id="comunidade-tabs" className="scroll-mt-16">
            <h2 className="mb-4 font-display text-xl font-bold md:text-2xl">Na comunidade</h2>
            <CommunityTabs data={communityQ.data} loading={communityQ.isLoading} />
          </section>

          <section id="precos-comunidade" className="scroll-mt-16">
            <div className="mb-4 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
              <div className="min-w-0">
                <h2 className="font-display text-xl font-bold md:text-2xl">
                  Quanto a comunidade pagou
                </h2>
                {pricingQ.data && pricingQ.data.count > 0 ? (
                  <p className="mt-0.5 text-sm text-muted-foreground">Registros anônimos de colecionadores.</p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={handleContributePrice}
                className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-full bg-primary px-4 text-sm font-bold text-primary-foreground hover:opacity-90"
              >
                <TrendingUp className="h-4 w-4" />
                <span className="hidden sm:inline">
                  {mine ? "Informar meu valor" : "Adicionar valor pago"}
                </span>
                <span className="sm:hidden">Informar</span>
              </button>
            </div>

            {pricingQ.isLoading ? (
              <div className="h-56 animate-pulse rounded-[26px] border border-border bg-surface/40" />
            ) : pricingQ.data && pricingQ.data.count > 0 ? (
              <Suspense
                fallback={
                  <div className="h-64 animate-pulse rounded-[26px] border border-border bg-surface/40" />
                }
              >
                <CommunityPricingChart data={pricingQ.data} />
              </Suspense>
            ) : (
              <div className="flex items-center gap-3 rounded-xl border border-dashed border-border bg-surface/35 px-4 py-4">
                <TrendingUp className="h-4 w-4 shrink-0 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Nenhum valor informado ainda.</p>
              </div>
            )}
          </section>





          <section id="comentarios" className="scroll-mt-16">
            <h2 className="mb-4 font-display text-xl font-bold md:text-2xl">Comentários</h2>
            <Suspense fallback={<div className="h-24 animate-pulse rounded-2xl border border-border bg-surface/40" />}>
              <AlbumComments discogsId={albumId} />
            </Suspense>
          </section>
        </div>



        <aside id="informacoes" className="min-w-0 space-y-4 scroll-mt-16 lg:sticky lg:top-24 lg:self-start">
          <AlbumTechnicalSidebar album={album} />

          <Link

            to="/explore"
            className="block rounded-2xl border border-border bg-surface p-5 text-sm text-muted-foreground hover:border-primary/50"
          >
            ← Voltar para Explorar
          </Link>
        </aside>
      </div>

      {/* Action details dialog (owned / for_sale / for_trade) */}
      <ResponsiveDialog open={actionDialog !== null} onOpenChange={(o: boolean) => !o && setActionDialog(null)}>
        <ResponsiveDialogContent desktopClassName="max-w-md p-6" mobileClassName="p-5">
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle>
              {actionDialog === "owned" && "Adicionar à coleção"}
              {actionDialog === "for_sale" && "Colocar à venda"}
              {actionDialog === "for_trade" && "Disponibilizar para troca"}
            </ResponsiveDialogTitle>
          </ResponsiveDialogHeader>
          <div className="mt-4 space-y-3">
            {actionDialog === "owned" && (
              <>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  placeholder="Preço pago R$ (opcional)"
                  value={paidPrice}
                  onChange={(e) => setPaidPrice(e.target.value)}
                  className="h-10 w-full rounded-lg border border-input bg-surface px-3 text-sm"
                />
                <input
                  type="date"
                  value={purchasedAt}
                  onChange={(e) => setPurchasedAt(e.target.value)}
                  className="h-10 w-full rounded-lg border border-input bg-surface px-3 text-sm"
                />
              </>
            )}
            {actionDialog === "for_sale" && (
              <>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  placeholder="Preço de venda R$"
                  value={salePrice}
                  onChange={(e) => setSalePrice(e.target.value)}
                  className="h-10 w-full rounded-lg border border-input bg-surface px-3 text-sm"
                />
                <input
                  type="text"
                  placeholder="Descrição (opcional)"
                  value={saleDescription}
                  onChange={(e) => setSaleDescription(e.target.value)}
                  className="h-10 w-full rounded-lg border border-input bg-surface px-3 text-sm"
                />
              </>
            )}
            {actionDialog === "for_trade" && (
              <input
                type="text"
                placeholder="O que aceita em troca? (opcional)"
                value={saleDescription}
                onChange={(e) => setSaleDescription(e.target.value)}
                className="h-10 w-full rounded-lg border border-input bg-surface px-3 text-sm"
              />
            )}
            <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-border bg-surface/60 p-3">
              <input
                type="checkbox"
                checked={shareThought}
                onChange={(event) => setShareThought(event.target.checked)}
                className="mt-0.5 h-4 w-4 accent-[color:var(--action)]"
              />
              <span>
                <span className="block text-sm font-semibold">Compartilhar um pensamento no Feed</span>
                <span className="mt-0.5 block text-xs text-muted-foreground">O disco já aparecerá selecionado na publicação.</span>
              </span>
            </label>
          </div>
          <ResponsiveDialogFooter className="mt-4">
            <button
              onClick={() => setActionDialog(null)}
              className="rounded-full border border-border bg-surface px-4 py-2 text-sm font-semibold"
            >
              Cancelar
            </button>
            <button
              disabled={addMut.isPending}
              onClick={() => actionDialog && addMut.mutate(actionDialog)}
              className="inline-flex items-center gap-2 rounded-full gradient-primary px-5 py-2 text-sm font-bold text-primary-foreground disabled:opacity-50"
            >
              {addMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Confirmar
            </button>
          </ResponsiveDialogFooter>
        </ResponsiveDialogContent>
      </ResponsiveDialog>

      {/* Community price contribution dialog */}
      <ResponsiveDialog open={priceDialog} onOpenChange={setPriceDialog}>
        <ResponsiveDialogContent desktopClassName="max-w-md p-6" mobileClassName="p-5">
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle>Quanto você pagou?</ResponsiveDialogTitle>
          </ResponsiveDialogHeader>
          <p className="mt-2 text-sm text-muted-foreground">
            Compartilhamos de forma anônima — só o mês e o estado de conservação aparecem
            para a comunidade.
          </p>
          <div className="mt-4 space-y-3">
            <input
              type="number"
              min={0}
              step="0.01"
              placeholder="Valor pago R$"
              value={paidPrice}
              onChange={(e) => setPaidPrice(e.target.value)}
              className="h-11 w-full rounded-lg border border-input bg-surface px-3 text-sm"
            />
            <input
              type="date"
              value={purchasedAt}
              onChange={(e) => setPurchasedAt(e.target.value)}
              className="h-11 w-full rounded-lg border border-input bg-surface px-3 text-sm"
            />
          </div>
          <ResponsiveDialogFooter className="mt-4">
            <button
              onClick={() => setPriceDialog(false)}
              className="rounded-full border border-border bg-surface px-4 py-2 text-sm font-semibold"
            >
              Cancelar
            </button>
            <button
              disabled={priceMut.isPending || !paidPrice}
              onClick={() => priceMut.mutate()}
              className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm font-bold text-primary-foreground disabled:opacity-50"
            >
              {priceMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Salvar valor
            </button>
          </ResponsiveDialogFooter>
        </ResponsiveDialogContent>
      </ResponsiveDialog>

      {/* Auth required dialog */}
      <ResponsiveDialog open={authOpen} onOpenChange={setAuthOpen}>
        <ResponsiveDialogContent desktopClassName="max-w-sm p-6" mobileClassName="p-5">
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle>Entre para continuar</ResponsiveDialogTitle>
          </ResponsiveDialogHeader>
          <p className="mt-3 text-sm text-muted-foreground">
            Faça login ou crie uma conta para adicionar discos à sua coleção,
            wishlist ou marketplace.
          </p>
          <ResponsiveDialogFooter className="mt-4 flex-col gap-2 sm:flex-row">
            <button
              onClick={() => setAuthOpen(false)}
              className="rounded-full border border-border bg-surface px-4 py-2 text-sm font-semibold"
            >
              Agora não
            </button>
            <button
              onClick={() => {
                setAuthOpen(false);
                navigate({ to: "/auth" });
              }}
              className="rounded-full gradient-primary px-5 py-2 text-sm font-bold text-primary-foreground"
            >
              Entrar / Cadastrar
            </button>
          </ResponsiveDialogFooter>
        </ResponsiveDialogContent>
      </ResponsiveDialog>
    </AppShell>
  );
}

function ActionButton({
  label,
  Icon,
  active,
  pending,
  onClick,
  primary,
}: {
  action: Action;
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
  active: boolean;
  pending: boolean;
  onClick: () => void;
  primary?: boolean;
}) {
  const base =
    "inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors disabled:opacity-50";
  const activeCls =
    "border border-primary/40 bg-primary/15 text-primary hover:bg-primary/25";
  const primaryCls =
    "gradient-primary text-primary-foreground font-bold hover:opacity-90";
  const idleCls =
    "border border-border bg-surface hover:border-primary/50";
  const cls = active ? activeCls : primary ? primaryCls : idleCls;
  const IconEl = pending ? Loader2 : active ? X : Icon;
  return (
    <button onClick={onClick} disabled={pending} className={`${base} ${cls}`}>
      <IconEl className={`h-4 w-4 ${pending ? "animate-spin" : ""}`} />
      {active ? "Remover" : label}
    </button>
  );
}

type CommunityData = Awaited<ReturnType<typeof getAlbumCommunity>>;

function CommunityTabs({ data, loading }: { data: CommunityData | undefined; loading: boolean }) {
  const [tab, setTab] = useState<"owned" | "wishlist" | "for_trade" | "for_sale">(
    (data?.counts.for_sale ?? 0) > 0 ? "for_sale" : (data?.counts.for_trade ?? 0) > 0 ? "for_trade" : "owned",
  );

  const c = data?.counts ?? { owned: 0, wishlist: 0, for_sale: 0, for_trade: 0 };

  const tabs = [
    { key: "for_sale" as const, label: "Vendendo", icon: Tag, count: c.for_sale },
    { key: "for_trade" as const, label: "Trocando", icon: Repeat2, count: c.for_trade },
    { key: "owned" as const, label: "Quem tem", icon: UsersIcon, count: c.owned },
    { key: "wishlist" as const, label: "Quem quer", icon: Heart, count: c.wishlist },
  ];


  const emptyText: Record<typeof tab, string> = {
    owned: "Ninguém adicionou esse disco à coleção ainda. Seja o primeiro!",
    wishlist: "Ninguém marcou esse disco como desejo ainda.",
    for_trade: "Nenhum colecionador disponibilizou para troca ainda.",
    for_sale: "Nenhum colecionador colocou à venda ainda.",
  };

  const list =
    tab === "owned"
      ? data?.owners ?? []
      : tab === "wishlist"
        ? data?.wishers ?? []
        : tab === "for_trade"
          ? data?.traders ?? []
          : [];

  return (
    <div className="rounded-2xl border border-border bg-surface p-2">
      <div className="flex flex-wrap gap-1 border-b border-border p-1">
        {tabs.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                active
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-surface hover:text-foreground"
              }`}
            >
              <t.icon className="h-3.5 w-3.5" />
              {t.label}
              <span
                className={`rounded-full px-1.5 text-[10px] font-bold ${
                  active ? "bg-primary-foreground/20" : "bg-muted"
                }`}
              >
                {t.count}
              </span>
            </button>
          );
        })}
      </div>

      <div className="p-4">
        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-12 animate-pulse rounded-lg bg-muted/50" />
            ))}
          </div>
        ) : tab === "for_sale" ? (
          data && data.sellers.length > 0 ? (
            <ul className="divide-y divide-border">
              {data.sellers.map((s, idx) => {
                const price = s.sale_price ?? s.price_brl;
                const name = s.seller?.display_name || s.seller?.username || "Vendedor";
                const initials = (name[0] || "?").toUpperCase();
                return (
                  <li key={s.id} className="flex items-center gap-3 py-3">
                    {s.seller?.username ? (
                      <Link
                        to="/profile/$username"
                        params={{ username: s.seller.username }}
                        className="flex flex-1 items-center gap-3 hover:opacity-80"
                      >
                        <Avatar className="h-10 w-10">
                          {s.seller?.avatar_url ? <AvatarImage src={s.seller.avatar_url} alt={name} /> : null}
                          <AvatarFallback>{initials}</AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-semibold">{name}</div>
                          <div className="truncate text-xs text-muted-foreground">
                            {[s.condition, s.seller?.city].filter(Boolean).join(" · ") || "Vinil à venda"}
                          </div>
                        </div>
                      </Link>
                    ) : (
                      <div className="flex flex-1 items-center gap-3">
                        <Avatar className="h-10 w-10"><AvatarFallback>{initials}</AvatarFallback></Avatar>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-semibold">{name}</div>
                        </div>
                      </div>
                    )}
                    <div className="flex flex-col items-end gap-1">
                      <div className="text-sm font-bold">
                        {price != null
                          ? Number(price).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
                          : "Consultar"}
                      </div>
                      {idx === 0 && price != null && data.sellers.length > 1 && (
                        <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
                          Menor preço
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <EmptyCommunity text={emptyText.for_sale} />
          )
        ) : list.length > 0 ? (
          <ul className="divide-y divide-border">
            {list.map((m) => {
              const name = m.profile?.display_name || m.profile?.username || "Colecionador";
              const initials = (name[0] || "?").toUpperCase();
              return (
                <li key={m.id} className="py-2.5">
                  {m.profile?.username ? (
                    <Link
                      to="/profile/$username"
                      params={{ username: m.profile.username }}
                      className="flex items-center gap-3 hover:opacity-80"
                    >
                      <Avatar className="h-10 w-10">
                        {m.profile.avatar_url ? <AvatarImage src={m.profile.avatar_url} alt={name} /> : null}
                        <AvatarFallback>{initials}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold">{name}</div>
                        {m.profile.city && (
                          <div className="truncate text-xs text-muted-foreground">{m.profile.city}</div>
                        )}
                      </div>
                    </Link>
                  ) : (
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10"><AvatarFallback>{initials}</AvatarFallback></Avatar>
                      <div className="text-sm font-semibold">{name}</div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          <EmptyCommunity text={emptyText[tab]} />
        )}
      </div>
    </div>
  );
}

function EmptyCommunity({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-dashed border-border bg-background/40 px-4 py-4">
      <UsersIcon className="h-4 w-4 shrink-0 text-muted-foreground/60" />
      <p className="text-sm text-muted-foreground">{text}</p>
    </div>
  );
}

function AvailabilityHighlight({ data, loading }: { data: CommunityData | undefined; loading: boolean }) {
  if (loading || !data) return null;
  const sellers = data.sellers.filter((s) => s.listing === "for_sale");
  const traders = data.counts.for_trade;
  if (sellers.length === 0 && traders === 0) return null;
  const bestPrice = sellers
    .map((s) => Number(s.sale_price ?? s.price_brl ?? NaN))
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => a - b)[0];
  return (
    <div className="mt-6 flex flex-wrap items-center gap-2 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 px-4 py-3">
      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-emerald-500">
        Disponível agora
      </span>
      {bestPrice != null && (
        <span className="text-sm font-semibold">
          Menor preço:{" "}
          <span className="text-emerald-500">
            {bestPrice.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
          </span>
        </span>
      )}
      {sellers.length > 0 && (
        <span className="text-xs text-muted-foreground">
          {sellers.length} {sellers.length === 1 ? "vendendo" : "vendendo"}
        </span>
      )}
      {traders > 0 && (
        <span className="text-xs text-muted-foreground">
          · {traders} {traders === 1 ? "trocando" : "trocando"}
        </span>
      )}
      <a href="#comunidade-tabs" onClick={(e) => {
        e.preventDefault();
        document.getElementById("comunidade-tabs")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }} className="ml-auto text-xs font-semibold text-primary hover:underline">
        Ver quem →
      </a>
    </div>
  );
}

// ─────────────────────────────────────────── Nota da comunidade (hero) ───
function AlbumScoreHero({
  discogsId,
  trackCount,
  className,
}: {
  discogsId: string;
  trackCount: number;
  className?: string;
}) {
  const fn = useServerFn(getAlbumRating);
  const { data } = useQuery({
    queryKey: ["album-rating", discogsId],
    queryFn: () => fn({ data: { discogsId } }),
    staleTime: 60_000,
  });

  const hasScore = data?.score != null;

  return (
    <a
      href="#tracklist"
      className={cn(
        hasScore
          ? "mt-6 inline-flex items-center gap-4 rounded-2xl border border-border/60 bg-surface/70 px-4 py-3 backdrop-blur transition-colors hover:border-foreground/30"
          : "mt-4 inline-flex items-center gap-2 rounded-full border border-border/60 bg-surface/55 px-3.5 py-2 text-sm transition-colors hover:border-foreground/30",
        className,
      )}
    >
      {hasScore ? (
        <>
          <div className="flex items-baseline gap-1">
            <span className="font-display text-4xl font-bold leading-none text-amber-400">
              {data?.score?.toFixed(1).replace(".", ",")}
            </span>
            <span className="text-sm text-muted-foreground">/5</span>
          </div>
          <div className="flex flex-col">
            <span className="inline-flex items-center gap-1 text-[13px] font-semibold text-foreground">
              <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" strokeWidth={0} />
              Nota da comunidade
            </span>
            <span className="text-[11px] text-muted-foreground">
              {`${data?.ratedTracks ?? 0} ${(data?.ratedTracks ?? 0) === 1 ? "faixa avaliada" : "faixas avaliadas"} · ${data?.raters ?? 0} ${(data?.raters ?? 0) === 1 ? "pessoa" : "pessoas"}`}
            </span>
          </div>
        </>
      ) : (
        <>
          <Star className="h-4 w-4 text-muted-foreground" />
          <span className="font-semibold">{trackCount > 0 ? "Avalie as faixas" : "Ainda sem avaliações"}</span>
        </>
      )}
    </a>
  );
}

function AlbumTechnicalSidebar({ album }: { album: DiscogsAlbum }) {
  const labels = album.labels?.length
    ? album.labels
    : album.label
      ? [{ name: album.label, catalog: album.catalog }]
      : [];
  const facts = [
    { label: "Ano", value: album.year ? String(album.year) : "", Icon: CalendarDays },
    { label: "País", value: album.country, Icon: Globe2 },
    { label: "Formato", value: album.format, Icon: LibraryBig },
    { label: "Gêneros", value: album.genres?.join(" · ") || album.genre, Icon: Disc3 },
  ].filter((item) => item.value);
  const creditGroups = groupCredits(album.credits ?? []);

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-surface">
      <div className="border-b border-border px-5 py-4">
        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Edição</p>
        <h2 className="mt-1 font-display text-lg font-semibold">Ficha do vinil</h2>
      </div>
      <div className="grid grid-cols-2 gap-px bg-border">
        {facts.map(({ label, value, Icon }) => (
          <div key={label} className="min-w-0 bg-surface px-4 py-3">
            <dt className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
              <Icon className="h-3 w-3" /> {label}
            </dt>
            <dd className="mt-1 break-words text-sm font-medium leading-snug">{value}</dd>
          </div>
        ))}
      </div>

      {labels.length > 0 ? (
        <div className="border-t border-border px-5 py-4">
          <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Selos e catálogo</p>
          <div className="space-y-2">
            {labels.map((label, index) => (
              <div key={`${label.name}-${label.catalog}-${index}`} className="flex justify-between gap-3 text-sm">
                <span className="font-medium">{label.name}</span>
                {label.catalog ? <span className="text-right text-muted-foreground">{label.catalog}</span> : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <Accordion type="multiple" className="border-t border-border px-5">
        {album.pressings && album.pressings.length > 0 ? (
          <AccordionItem value="pressings" className="border-border">
            <AccordionTrigger className="hover:no-underline">
              <span className="flex items-center gap-2"><LibraryBig className="h-4 w-4" /> Prensagens <span className="text-xs text-muted-foreground">{album.pressings.length}</span></span>
            </AccordionTrigger>
            <AccordionContent>
              <div className="max-h-72 space-y-1 overflow-y-auto pr-1">
                {album.pressings.map((pressing) => (
                  <Link
                    key={pressing.id}
                    to="/album/$id"
                    params={{ id: buildAlbumParam(pressing.id, album.artist, album.title) }}
                    className={cn(
                      "block rounded-lg border px-3 py-2.5 transition-colors hover:border-primary/40",
                      pressing.isCurrent ? "border-primary/30 bg-primary/5" : "border-border bg-background/35",
                    )}
                  >
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <span className="font-semibold">{[pressing.country, pressing.year || "s/ ano"].filter(Boolean).join(" · ")}</span>
                      {pressing.isCurrent ? <span className="text-[10px] font-bold uppercase text-primary">Esta edição</span> : null}
                    </div>
                    <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                      {[pressing.label, pressing.catalog, pressing.format].filter(Boolean).join(" · ")}
                    </div>
                  </Link>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        ) : null}

        {creditGroups.map((group) => (
          <AccordionItem key={group.label} value={group.label} className="border-border last:border-b-0">
            <AccordionTrigger className="hover:no-underline">
              <span className="flex items-center gap-2"><group.Icon className="h-4 w-4" /> {group.label} <span className="text-xs text-muted-foreground">{group.items.length}</span></span>
            </AccordionTrigger>
            <AccordionContent>
              <dl className="space-y-3">
                {group.items.map((credit) => (
                  <div key={credit.role}>
                    <dt className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">{credit.role}</dt>
                    <dd className="mt-0.5 text-sm leading-relaxed">{credit.names.join(", ")}</dd>
                  </div>
                ))}
              </dl>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

function groupCredits(credits: NonNullable<DiscogsAlbum["credits"]>) {
  const groups = [
    { label: "Integrantes e músicos", Icon: Mic2, pattern: /vocals?|guitar|bass|drums?|percussion|piano|keyboards?|saxophone|trumpet|violin|cello|organ|synth|instrument|band|ensemble/i },
    { label: "Produção e composição", Icon: SlidersHorizontal, pattern: /produc|written|compos|songwriter|lyrics|music by|arrang|conduct|master|mix|record|engineer/i },
    { label: "Arte e fotografia", Icon: Palette, pattern: /art|design|photo|illustr|layout|cover/i },
  ];
  const bucket = new Map<string, typeof credits>();
  for (const group of groups) bucket.set(group.label, []);
  bucket.set("Outros créditos", []);
  for (const credit of credits) {
    const group = groups.find((item) => item.pattern.test(credit.role));
    bucket.get(group?.label ?? "Outros créditos")?.push(credit);
  }
  return [
    ...groups,
    { label: "Outros créditos", Icon: UsersIcon, pattern: /.*/ },
  ]
    .map((group) => ({ ...group, items: bucket.get(group.label) ?? [] }))
    .filter((group) => group.items.length > 0);
}
